import { useState } from 'react';
import classNames from 'classnames';

import { Dropdown, DropdownMenuItem } from '@/components/dropdown';
import { Icon } from '@/components/icon';
import { IconButton, InlineButton } from '@/components/button';
import { Exploration, ExplorationTab } from '@/explore/types';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { Badge } from '@/components/badge';
import { SortableItemSimple } from '@/explore/components/sortable-item';

import { TabEditForm } from './tab-edit-form';
import { CellItem } from './cell-item';

import { getCellsForExplorationOutline } from './utils';

import styles, { activeBadgeBgColor } from './exploration-outline.module.scss';

type ExplorationOutlineTabProps = {
  tab: ExplorationTab;
  isActive: boolean;
  exploration: Exploration;
  draggedIndex: number | null;
  setDraggedIndex: (index: number | null) => void;
  onMoveCell: (fromIndex: number, toIndex: number, after: boolean) => void;
  onMoveCellToTab: (fromIndex: number) => void;
  onSelectCell: (cellId: string) => void;
  onAddCell: (index: number, tabId?: string) => void;
  onDeleteTab: (tabId: string) => void;
  onRenameTab: (tabId: string, name: string) => void;
  onDuplicateTab: (tabId: string) => void;
  isExpanded: boolean;
  onToggleCollapsed: () => void;
  renamingTabId: string | null;
  onSetRenamingTab: (tabId: string) => void;
  onClearRenamingTab: () => void;
};

export const ExplorationOutlineTab = (props: ExplorationOutlineTabProps) => {
  const {
    tab,
    isActive,
    exploration,
    draggedIndex,
    setDraggedIndex,
    onMoveCell,
    onMoveCellToTab,
    onSelectCell,
    onAddCell,
    onDeleteTab,
    onRenameTab,
    onDuplicateTab,
    isExpanded,
    onToggleCollapsed,
    renamingTabId,
    onSetRenamingTab,
    onClearRenamingTab,
  } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const cellsInTab = getCellsForExplorationOutline(exploration, tab.tabId);
  const isRenaming = renamingTabId === tab.tabId;

  const handleRename = (newName: string) => {
    onRenameTab(tab.tabId, newName);
    onClearRenamingTab();
  };

  const menuItems: DropdownMenuItem[] = [
    {
      label: 'Rename',
      onClick: () => onSetRenamingTab(tab.tabId),
      icon: <Icon name="Edit3" size={15} />,
    },
    {
      label: 'Duplicate',
      onClick: () => onDuplicateTab(tab.tabId),
      icon: <Icon name="Copy" size={15} />,
    },
    {
      label: 'Delete',
      onClick: () => setShowDeleteModal(true),
      icon: <Icon name="Trash2" size={15} />,
    },
  ];

  return (
    <div
      title={tab.label}
      className={classNames(styles.group, {
        [styles.collapsed]: !isExpanded,
      })}>
      <div
        className={classNames(styles.groupHeader, styles.sortableItem)}
        onClick={isRenaming ? undefined : onToggleCollapsed}>
        {isRenaming ? (
          <TabEditForm
            initialValue={tab.label}
            onSave={handleRename}
            onCancel={onClearRenamingTab}
          />
        ) : (
          <>
            <Icon name="DragHandle" size={10} className={styles.sortHandle} />
            <h4>{tab.label}</h4>
            {isActive && <Badge label="Current" size="small" bgColor={activeBadgeBgColor} />}
            <IconButton
              icon={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              className={styles.collapseIcon}
            />
            <Dropdown
              className={styles.menu}
              align="right"
              trigger={(isOpen, setIsOpen) => (
                <IconButton
                  className={styles.menuButton}
                  icon="MoreHorizontal"
                  title="More..."
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                />
              )}
              items={menuItems}
            />
          </>
        )}
      </div>
      <div className={classNames(styles.cells)}>
        <div>
          {cellsInTab.map((cell) => {
            const cellIndex = exploration.view.cells.findIndex((c) => c.id === cell.id);
            return (
              <CellItem
                key={cell.id}
                cell={cell}
                index={cellIndex}
                draggedIndex={draggedIndex}
                setDraggedIndex={setDraggedIndex}
                onMove={onMoveCell}
                onClickCell={onSelectCell}
              />
            );
          })}
          {cellsInTab.length === 0 && (
            <SortableItemSimple
              index={-1}
              scope="exploration-outline-cells"
              draggedIndex={draggedIndex}
              setDraggedIndex={setDraggedIndex}
              onReorder={onMoveCellToTab}
              className={classNames(styles.cellItem, styles.sortableItem)}
              classNames={{
                dragBefore: styles.dragBefore,
                dragAfter: styles.dragBefore,
              }}>
              <InlineButton onClick={() => onAddCell(cellsInTab.length, tab.tabId)} size="small">
                <Icon name="Plus" size={15} /> New block
              </InlineButton>
            </SortableItemSimple>
          )}
        </div>
      </div>
      {showDeleteModal && (
        <ConfirmationModal
          title={`Are you sure you want to delete "${tab.label}"?`}
          description="Deleting the tab will also delete all blocks within it!"
          submitLabel="Delete"
          onSubmit={async () => onDeleteTab(tab.tabId)}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};
